/**
 * EmailABTesting — A/B Subject Line section embedded inside the master
 * Email Analytics page (Iter77).
 *
 * Pulls /api/email-analytics/ab-tests, displays one card per supported
 * campaign with:
 *   • Subject A vs Subject B (editable via modal)
 *   • Sent, open rate, click rate per arm
 *   • Winner badge + confidence label (HIGH/MEDIUM/LOW/INSUFFICIENT)
 *   • Z-score and sample size annotation
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FlaskConical, Trophy, AlertTriangle, Edit3, Loader2, Save, X,
  TrendingUp, BarChart3,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;
const fmtInt = (v) => new Intl.NumberFormat("en-US").format(v || 0);

const CONFIDENCE_COLORS = {
  HIGH: "#10B981",
  MEDIUM: "#F59E0B",
  LOW: "#94A3B8",
  INSUFFICIENT: "#64748B",
};

export default function EmailABTesting() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {campaign, a, b}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email-analytics/ab-tests`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl border bg-white/[0.04] p-5"
         style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="email-ab-testing">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-[#C9A24E]" />
          <h2 className="text-[14px] font-bold text-white">A/B Testing · Subject lines</h2>
        </div>
        <span className="text-[11px] text-white/55">
          50/50 random split por padrão · Z-test de duas proporções (open rate)
        </span>
      </div>

      {loading && !data ? (
        <div className="py-8 text-center text-white/55 inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando experimentos…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data?.tests || []).map((t) => (
            <ABCard key={t.campaign} test={t} onEdit={() => setEditing(t)} />
          ))}
        </div>
      )}

      {editing && (
        <ABEditModal test={editing}
          placeholders={data?.available_placeholders || []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ABCard({ test, onEdit }) {
  const winner = test.winner;
  const cColor = CONFIDENCE_COLORS[test.confidence_label] || "#64748B";
  return (
    <div className="rounded-xl border border-white/10 bg-black/25 p-4"
         data-testid={`ab-card-${test.campaign}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-[12.5px] font-bold text-white truncate">{test.label}</h3>
        <button onClick={onEdit}
          data-testid={`ab-edit-${test.campaign}`}
          className="text-white/55 hover:text-[#C9A24E] p-1" title="Editar">
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="space-y-2">
        <ABArm arm={test.a} label="A" highlight={winner === "A"} />
        <ABArm arm={test.b} label="B" highlight={winner === "B"} />
      </div>

      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-[11px]">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold"
              style={{ background: `${cColor}1A`, color: cColor }}
              data-testid={`ab-confidence-${test.campaign}`}>
          {test.confidence_label === "INSUFFICIENT" ? <AlertTriangle className="w-3 h-3" /> : <BarChart3 className="w-3 h-3" />}
          {test.confidence_label}
          {test.confidence > 0 && ` ${(test.confidence * 100).toFixed(0)}%`}
        </span>
        <span className="text-white/55 font-mono">
          z={test.z_score?.toFixed(2)}
          {test.low_traffic && <span className="ml-1 text-amber-300">· low traffic</span>}
        </span>
      </div>

      {winner && !test.low_traffic && (
        <div className="mt-2 text-[11px] inline-flex items-center gap-1 text-emerald-300 font-bold"
             data-testid={`ab-winner-${test.campaign}`}>
          <Trophy className="w-3 h-3" /> Vencedor atual: <b>{winner}</b>
          <TrendingUp className="w-3 h-3" />
        </div>
      )}
      {test.low_traffic && (
        <div className="mt-2 text-[10.5px] text-white/55">
          Cada braço precisa de ≥{test.min_sample_each_arm} envios para significância.
        </div>
      )}
    </div>
  );
}

function ABArm({ arm, label, highlight }) {
  return (
    <div className={`rounded border p-2.5 transition-colors ${highlight
      ? "border-emerald-400/60 bg-emerald-500/10"
      : "border-white/10 bg-white/[0.03]"}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center
          ${highlight ? "bg-emerald-400 text-emerald-950" : "bg-white/10 text-white/70"}`}>{label}</span>
        <span className="text-[11.5px] text-white/85 font-medium truncate flex-1" title={arm.subject}>
          {arm.subject}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1 text-[10.5px]">
        <Metric label="sent" value={fmtInt(arm.sent)} />
        <Metric label="open" value={fmtPct(arm.open_rate)} accent="#3B82F6" />
        <Metric label="click" value={fmtPct(arm.click_rate)} accent="#10B981" />
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className="text-white/45 uppercase tracking-wider font-bold">{label}</div>
      <div className="font-mono font-bold" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

function ABEditModal({ test, placeholders, onClose, onSaved }) {
  const [a, setA] = useState({ subject: test.a.subject, weight: test.a.weight ?? 0.5 });
  const [b, setB] = useState({ subject: test.b.subject, weight: test.b.weight ?? 0.5 });
  const [active, setActive] = useState(test.active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (a.subject.trim().length < 3 || b.subject.trim().length < 3) {
      toast.error("Cada subject precisa de pelo menos 3 caracteres");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/email-analytics/ab-tests/${test.campaign}`,
        { a, b, active });
      toast.success(`A/B atualizado · ${test.label}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="ab-edit-modal">
      <div className="bg-[#0F0820] border border-white/15 rounded-xl p-5 max-w-xl w-full">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[15px] font-bold text-white inline-flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-[#C9A24E]" /> Editar A/B · {test.label}
          </h3>
          <button onClick={onClose} className="text-white/55 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11.5px] text-white/55 mb-4">
          Placeholders disponíveis: <code className="text-amber-200">{placeholders.join(" · ")}</code>
        </p>

        {[
          { label: "A", state: a, setter: setA, color: "#3B82F6" },
          { label: "B", state: b, setter: setB, color: "#A855F7" },
        ].map(({ label, state, setter, color }) => (
          <div key={label} className="mb-4">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                    style={{ background: color }}>{label}</span>
              <span className="text-[12px] font-bold text-white">Variant {label}</span>
            </div>
            <input type="text" value={state.subject}
              data-testid={`ab-input-${label}-subject`}
              onChange={(e) => setter({ ...state, subject: e.target.value })}
              placeholder="Subject template"
              className="w-full bg-black/35 border border-white/15 rounded px-3 h-9 text-[12.5px] text-white focus:border-[#C9A24E] outline-none" />
            <div className="flex items-center gap-2 mt-1.5 text-[11px] text-white/65">
              <span>Peso (0-1):</span>
              <input type="number" min="0" max="1" step="0.05"
                value={state.weight}
                data-testid={`ab-input-${label}-weight`}
                onChange={(e) => setter({ ...state, weight: Number(e.target.value) })}
                className="w-20 bg-black/35 border border-white/15 rounded px-2 h-7 text-white" />
            </div>
          </div>
        ))}

        <label className="flex items-center gap-2 mb-4 text-[12px] text-white/85 cursor-pointer">
          <input type="checkbox" checked={active}
            data-testid="ab-input-active"
            onChange={(e) => setActive(e.target.checked)}
            className="accent-[#C9A24E]" />
          Experimento ativo (envia variants nos disparos)
        </label>

        <div className="flex gap-2">
          <button onClick={save} disabled={saving}
            data-testid="ab-save"
            className="flex-1 h-10 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </button>
          <button onClick={onClose}
            className="px-5 h-10 rounded-lg border border-white/15 text-white text-[12.5px] font-bold">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
